import { T, Var, useGT } from 'gt-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { UploadIcon, XIcon, CheckIcon } from '@phosphor-icons/react';
import FileIcon from './FileIcon';
import type { UploadProgress } from 'src/types/UserDocuments';

interface UploadProgressListProps {
  uploadProgress: UploadProgress[];
  onRetryUpload?: (documentId: string, fileName: string) => void;
  onCancelUpload?: (documentId: string) => void;
}

/**
 * Per-file upload progress rows (name, progress bar, success/failure state,
 * optional retry/cancel actions). Extracted from FileUploadDropzone so upload
 * paths without the dropzone UI — e.g. the always-on collection-thread
 * dropzone's floating panel — render the exact same feedback.
 */
export function UploadProgressList({
  uploadProgress,
  onRetryUpload,
  onCancelUpload,
}: UploadProgressListProps) {
  const gt = useGT();

  return (
    <>
      {uploadProgress.map((progress) => (
        <Card key={progress.documentId} className="mb-4">
          <CardContent className="pb-4">
            <div className="flex items-center mb-1">
              <FileIcon
                fileName={progress.fileName}
                size="small"
                showBackground={false}
              />
              <div className="ml-1 flex-grow">
                <p className="text-sm font-medium">{progress.fileName}</p>
              </div>

              {progress.status === 'uploaded' && onCancelUpload && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => onCancelUpload(progress.documentId)}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}

              {progress.status === 'failed' && onRetryUpload && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700"
                  title={gt('Retry upload', {
                    context: 'tooltip for retry upload button',
                  })}
                  onClick={() =>
                    onRetryUpload(progress.documentId, progress.fileName)
                  }
                >
                  <UploadIcon className="h-4 w-4" />
                </Button>
              )}
            </div>

            {progress.status === 'uploading' && (
              <Progress value={progress.progress} className="h-1.5" />
            )}

            {progress.status === 'uploaded' && (
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center mr-2">
                  <CheckIcon className="w-2 h-2 text-white" />
                </div>
                <p className="text-xs text-green-600 ml-0.5">
                  <T context="status message when a file has been successfully uploaded">
                    Upload completed
                  </T>
                </p>
              </div>
            )}

            {progress.status === 'failed' && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center mr-2">
                    <XIcon className="w-2 h-2 text-white" />
                  </div>
                  <p className="text-xs text-red-600 ml-0.5">
                    {progress.error ||
                      gt('Upload failed', {
                        context:
                          'fallback error message when file upload fails',
                      })}
                  </p>
                </div>
                {onRetryUpload && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() =>
                      onRetryUpload(progress.documentId, progress.fileName)
                    }
                  >
                    <UploadIcon className="w-4 h-4 mr-1" />
                    <T context="button to retry a failed file upload">Retry</T>
                  </Button>
                )}
              </div>
            )}

            {progress.status === 'uploading' && (
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-600">
                  <T context="upload progress percentage indicator showing how much of the file has been uploaded">
                    <Var>{Math.round(progress.progress)}</Var>% uploaded
                  </T>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </>
  );
}
